import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ReportsSectionCompact.css";
import { useNavigate } from "react-router-dom";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import {
  getSuperAdminLiveDashboardFilters,
  postSuperAdminLiveDashboardReport,
} from "../api/superadminReports";
import {
  buildSuperAdminReportHelpGuide,
  getSuperAdminHelpAnchors,
} from "../constants/superAdminReportHelpGuides";
import { LIVE_DASHBOARD_TOOLTIPS } from "../constants/reportTooltips";
import { useAuth } from "../context/AuthContext";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";

const SCOPE_OPTIONS = [
  { value: "all", label: "All Salons" },
  { value: "business", label: "Business" },
  { value: "salon", label: "Salon" },
];

const HELP_GUIDE = buildSuperAdminReportHelpGuide({
  reportKey: "live-dashboard",
  title: "Live Dashboard (Super Admin)",
  subtitle:
    "View system-wide activity and KPIs for the selected scope and date range.",
  summaryLabel: "Live KPIs",
  detailsLabel: "Dashboard Details",
  hasExport: false,
});

const HELP_ANCHORS = getSuperAdminHelpAnchors("live-dashboard");

const formatDateISO = (value, timeZone) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  if (timeZone) {
    try {
      return new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(value);
    } catch (error) {
      // Fall through to local fallback.
    }
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const formatMoney = (value) => {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return "$0.00";
  return `$${number.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatMoneyPerMinute = (value) => `${formatMoney(value)}/min`;

const formatPercent = (value) => {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return "0.0%";
  return `${number.toFixed(1)}%`;
};

const formatSignedPercent = (value) => {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return "0.0%";
  return `${number > 0 ? "+" : ""}${number.toFixed(1)}%`;
};

const getBarLabel = (point, index, total) => {
  if (!point) return "";
  if (total <= 8) return point.start_date?.slice(5) || point.label;
  const stride = Math.ceil(total / 6);
  return index % stride === 0 ? point.start_date?.slice(5) || point.label : "";
};

export default function SuperAdminLiveDashboard() {
  const navigate = useNavigate();
  const { is_super_admin, salonTimezone: authSalonTimezone } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();

  const [scope, setScope] = useState("all");
  const [businessId, setBusinessId] = useState("");
  const [salonId, setSalonId] = useState("");
  const [includeBusinessSalonCols, setIncludeBusinessSalonCols] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filtersError, setFiltersError] = useState("");

  const [startDate, setStartDate] = useState(() =>
    formatDateISO(addDays(new Date(), -6), authSalonTimezone || "UTC")
  );
  const [endDate, setEndDate] = useState(() =>
    formatDateISO(new Date(), authSalonTimezone || "UTC")
  );
  const [bucket, setBucket] = useState("day");
  const [rebookWindowDays, setRebookWindowDays] = useState(30);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);
  const hasAutoRunRef = useRef(false);

  const broadScope = scope === "all" || scope === "business";
  const showEntityColumns = broadScope && includeBusinessSalonCols;

  const businessMap = useMemo(() => {
    const map = new Map();
    (businesses || []).forEach((entry) => {
      if (!entry) return;
      map.set(String(entry.id), entry);
    });
    return map;
  }, [businesses]);

  const salonMap = useMemo(() => {
    const map = new Map();
    (salons || []).forEach((entry) => {
      if (!entry) return;
      map.set(String(entry.id), entry);
    });
    return map;
  }, [salons]);

  const anchorTimezone = useMemo(() => {
    if (salonId) {
      const selectedSalon = salonMap.get(String(salonId));
      if (selectedSalon?.timezone) return selectedSalon.timezone;
    }
    if (businessId) {
      const selectedBusiness = businessMap.get(String(businessId));
      if (selectedBusiness?.timezone) return selectedBusiness.timezone;
    }
    return authSalonTimezone || "UTC";
  }, [salonId, salonMap, businessId, businessMap, authSalonTimezone]);

  const filteredSalons = useMemo(() => {
    if (!businessId) return salons;
    return (salons || []).filter(
      (entry) => String(entry.business_id || "") === String(businessId)
    );
  }, [salons, businessId]);

  const loadFilters = useCallback(async () => {
    if (!is_super_admin) return;
    setFiltersLoading(true);
    setFiltersError("");
    try {
      const data = await getSuperAdminLiveDashboardFilters({
        scope,
        business_id: businessId || undefined,
        salon_id: salonId || undefined,
      });
      setBusinesses(Array.isArray(data?.businesses) ? data.businesses : []);
      setSalons(Array.isArray(data?.salons) ? data.salons : []);
    } catch (err) {
      setBusinesses([]);
      setSalons([]);
      setFiltersError(err?.message || "Unable to load report filters.");
    } finally {
      setFiltersLoading(false);
    }
  }, [is_super_admin, scope, businessId, salonId]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    setIncludeBusinessSalonCols(scope !== "salon");
    hasAutoRunRef.current = false;
  }, [scope]);

  useEffect(() => {
    if (!salonId || !businessId) return;
    const selectedSalon = salonMap.get(String(salonId));
    if (!selectedSalon) {
      setSalonId("");
      return;
    }
    if (String(selectedSalon.business_id || "") !== String(businessId)) {
      setSalonId("");
    }
  }, [businessId, salonId, salonMap]);

  const dateRangeError = useMemo(() => {
    if (!startDate || !endDate) return "Start and end dates are required.";
    if (endDate < startDate) return "End date cannot be before start date.";
    return "";
  }, [startDate, endDate]);

  const payload = useMemo(
    () => ({
      scope,
      business_id: businessId ? Number(businessId) : undefined,
      salon_id: salonId ? Number(salonId) : undefined,
      start_date: startDate,
      end_date: endDate,
      bucket,
      rebook_window_days: Math.max(Number(rebookWindowDays) || 0, 0),
      include_business_salon_cols: showEntityColumns,
    }),
    [
      scope,
      businessId,
      salonId,
      startDate,
      endDate,
      bucket,
      rebookWindowDays,
      showEntityColumns,
    ]
  );

  const validateScope = useCallback(() => {
    if (scope === "business" && !businessId) {
      return "Choose a business for Business scope.";
    }
    if (scope === "salon" && !salonId) {
      return "Choose a salon for Salon scope.";
    }
    if (dateRangeError) {
      return dateRangeError;
    }
    return "";
  }, [scope, businessId, salonId, dateRangeError]);

  const fetchReport = useCallback(
    async (payloadOverride = null) => {
      const validationError = validateScope();
      if (validationError) {
        setError(validationError);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const data = await postSuperAdminLiveDashboardReport(payloadOverride || payload);
        setReport(data || null);
      } catch (err) {
        setReport(null);
        setError(err?.message || "Unable to load live dashboard.");
      } finally {
        setLoading(false);
      }
    },
    [payload, validateScope]
  );

  useEffect(() => {
    if (!is_super_admin || hasAutoRunRef.current) return;
    if (scope === "business" && !businessId) return;
    if (scope === "salon" && !salonId) return;
    if (dateRangeError) return;
    hasAutoRunRef.current = true;
    fetchReport(payload);
  }, [is_super_admin, scope, businessId, salonId, dateRangeError, fetchReport, payload]);

  const handleScopeChange = (value) => {
    setScope(value);
    if (value === "all") {
      setSalonId("");
    }
    if (value === "business") {
      setSalonId("");
    }
  };

  const handleBusinessChange = (value) => {
    setBusinessId(value);
    if (!value && scope !== "salon") {
      setSalonId("");
    }
    if (!salonId) return;
    const selectedSalon = salonMap.get(String(salonId));
    if (
      selectedSalon &&
      value &&
      String(selectedSalon.business_id || "") !== String(value)
    ) {
      setSalonId("");
    }
  };

  const handleSalonChange = (value) => {
    setSalonId(value);
    if (!value) return;
    const selectedSalon = salonMap.get(String(value));
    if (selectedSalon?.business_id) {
      setBusinessId(String(selectedSalon.business_id));
    }
  };

  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip text={LIVE_DASHBOARD_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  const revenueSeries = Array.isArray(report?.revenue_series) ? report.revenue_series : [];
  const maxRevenue = Math.max(
    1,
    ...revenueSeries.map((point) => Number(point?.value || 0))
  );
  const noTicketData = !loading && !error && report && Number(report.ticket_count || 0) === 0;

  const timezoneLine = useMemo(() => {
    if (scope === "salon") {
      const selectedTimezone =
        salonMap.get(String(salonId))?.timezone || report?.tz_name || anchorTimezone || "UTC";
      return `Salon timezone: ${selectedTimezone}`;
    }
    return "Dates use each salon's local timezone.";
  }, [scope, salonMap, salonId, report?.tz_name, anchorTimezone]);

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader title="Live Dashboard" closeTo="/superadmin/reports" />

          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div
              id={HELP_ANCHORS.overview}
              className={`rounded-2xl ${getSectionClassName(HELP_ANCHORS.overview)}`}
            >
              <ReportHelpCard
                title={HELP_GUIDE.title}
                subtitle={HELP_GUIDE.subtitle}
                sections={HELP_GUIDE.sections}
                troubleshooting={HELP_GUIDE.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            {filtersError ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-700">
                {filtersError}
              </div>
            ) : null}

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            ) : null}

            <div
              id={HELP_ANCHORS.filters}
              className={`rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4 ${getSectionClassName(
                HELP_ANCHORS.filters
              )}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  {renderFieldLabel("Scope", "scope")}
                  <select
                    value={scope}
                    onChange={(event) => handleScopeChange(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={filtersLoading}
                  >
                    {SCOPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Business", "business")}
                  <select
                    value={businessId}
                    onChange={(event) => handleBusinessChange(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={filtersLoading || scope === "salon"}
                  >
                    <option value="">All Businesses</option>
                    {(businesses || []).map((business) => (
                      <option key={business.id} value={business.id}>
                        {business.code} - {business.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Salon", "salon")}
                  <select
                    value={salonId}
                    onChange={(event) => handleSalonChange(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={filtersLoading || !(scope === "salon" || scope === "business")}
                  >
                    <option value="">
                      {scope === "salon"
                        ? "Select salon"
                        : scope === "business"
                        ? "All salons in business"
                        : "Salon filter disabled for this scope"}
                    </option>
                    {(filteredSalons || []).map((salon) => (
                      <option key={salon.id} value={salon.id}>
                        {salon.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="rounded-2xl border border-slate-200 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <input
                      id="live-dashboard-include-business-salon-cols"
                      type="checkbox"
                      checked={includeBusinessSalonCols}
                      onChange={(event) =>
                        setIncludeBusinessSalonCols(Boolean(event.target.checked))
                      }
                      disabled={!broadScope}
                    />
                    <label
                      htmlFor="live-dashboard-include-business-salon-cols"
                      className="text-sm font-medium text-slate-700"
                    >
                      Show Business/Salon columns
                    </label>
                    <InfoTooltip
                      text={LIVE_DASHBOARD_TOOLTIPS.include_business_salon_cols}
                      label="Show Business/Salon columns"
                    />
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    {broadScope
                      ? "Shown in Top Services rows for broad scopes."
                      : "Only available in All Salons or Business scope."}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  {renderFieldLabel("Start Date", "start_date")}
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("End Date", "end_date")}
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Bucket", "bucket")}
                  <select
                    value={bucket}
                    onChange={(event) => setBucket(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    <option value="day">Day</option>
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Rebook Window (days)", "rebooking_rate")}
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={rebookWindowDays}
                    onChange={(event) => setRebookWindowDays(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
              </div>

              {dateRangeError ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                  {dateRangeError}
                </div>
              ) : null}

              <div className="flex flex-wrap items-center gap-3">
                <div className="text-xs text-slate-500">{timezoneLine}</div>
                <div className="ml-auto flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => fetchReport(payload)}
                    className={`${BTN_PRIMARY} min-w-[140px]`}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Run Report"}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/superadmin/reports")}
                    className={`${BTN_OUTLINE} min-w-[140px]`}
                    disabled={loading}
                  >
                    Back to Reports
                  </button>
                </div>
              </div>
            </div>

            <div
              id={HELP_ANCHORS.export}
              className={`rounded-2xl border border-slate-200 bg-white p-4 ${getSectionClassName(
                HELP_ANCHORS.export
              )}`}
            >
              <h3 className="text-base font-semibold text-slate-900">Export</h3>
              <p className="mt-1 text-sm text-slate-600">
                This report does not support export.
              </p>
            </div>

            {noTicketData ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6 text-slate-600">
                No tickets found for this date range.
              </div>
            ) : null}

            <div
              id={HELP_ANCHORS.kpis}
              className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 ${getSectionClassName(
                HELP_ANCHORS.kpis
              )}`}
            >
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                  <span>Sales</span>
                  <InfoTooltip text={LIVE_DASHBOARD_TOOLTIPS.sales} label="Sales" />
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {formatMoney(report?.sales)}
                </div>
                <div className="mt-2 text-sm text-slate-600 flex items-center gap-1.5">
                  <span>{formatSignedPercent(report?.sales_change_pct_vs_prev)} vs previous period</span>
                  <InfoTooltip
                    text={LIVE_DASHBOARD_TOOLTIPS.sales_change}
                    label="Previous period change"
                  />
                </div>
                <div className="mt-1 text-sm text-slate-500 flex items-center gap-1.5">
                  <span>Avg ticket {formatMoney(report?.avg_ticket)}</span>
                  <InfoTooltip text={LIVE_DASHBOARD_TOOLTIPS.avg_ticket} label="Avg ticket" />
                </div>
                <div className="mt-1 text-sm text-slate-500">
                  {Number(report?.ticket_count || 0)} tickets
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                  <span>Rebooking</span>
                  <InfoTooltip text={LIVE_DASHBOARD_TOOLTIPS.rebooking_rate} label="Rebooking rate" />
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {formatPercent(report?.rebooking_rate_pct)}
                </div>
                <div className="mt-2 text-sm text-slate-500">30-day return rate</div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                  <span>Revenue / Booked Min</span>
                  <InfoTooltip
                    text={LIVE_DASHBOARD_TOOLTIPS.revenue_per_booked_minute}
                    label="Revenue per booked minute"
                  />
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {formatMoneyPerMinute(report?.revenue_per_booked_minute)}
                </div>
                <div className="mt-2 text-sm text-slate-500">
                  Net sales divided by booked appointment minutes
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                  <span>Staff Utilization</span>
                  <InfoTooltip text={LIVE_DASHBOARD_TOOLTIPS.utilization} label="Staff utilization" />
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {formatPercent(report?.utilization_pct)}
                </div>
                <div className="mt-2 text-sm text-slate-500">Booked vs available hours</div>
              </div>
            </div>

            <div
              id={HELP_ANCHORS.details}
              className={`space-y-4 rounded-2xl ${getSectionClassName(HELP_ANCHORS.details)}`}
            >
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
              <div className="flex items-center gap-1.5 text-base md:text-lg font-semibold text-slate-900">
                <span>Revenue trend</span>
                <InfoTooltip text={LIVE_DASHBOARD_TOOLTIPS.revenue_trend} label="Revenue trend" />
              </div>
              <div className="rounded-2xl border border-rose-100 bg-rose-50/60 p-4">
                <div className="h-52 flex items-end gap-2">
                  {revenueSeries.length ? (
                    revenueSeries.map((point, index) => {
                      const value = Number(point?.value || 0);
                      const height = Math.max(4, Math.round((value / maxRevenue) * 100));
                      return (
                        <div
                          key={`${point.start_date}-${point.end_date}-${index}`}
                          className="flex-1 min-w-0 flex flex-col items-center gap-2"
                        >
                          <div className="w-full flex items-end h-40">
                            <div
                              className="w-full rounded-t-xl bg-rose-400 transition-all"
                              style={{ height: `${height}%` }}
                              title={`${point.label}: ${formatMoney(value)}`}
                            />
                          </div>
                          <div className="text-[11px] leading-tight text-slate-500 text-center min-h-[1rem]">
                            {getBarLabel(point, index, revenueSeries.length)}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="w-full h-full grid place-items-center text-slate-500 text-sm">
                      {loading ? "Loading trend..." : "No trend data in this range."}
                    </div>
                  )}
                </div>
              </div>
            </div>

              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-1.5 text-base md:text-lg font-semibold text-slate-900">
                <span>Top Services</span>
                <InfoTooltip text={LIVE_DASHBOARD_TOOLTIPS.top_services} label="Top services" />
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm md:text-base">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      {showEntityColumns ? (
                        <>
                          <th className="px-4 py-3 text-left font-semibold">Business</th>
                          <th className="px-4 py-3 text-left font-semibold">Salon</th>
                        </>
                      ) : null}
                      <th className="px-4 py-3 text-left font-semibold">Service</th>
                      <th className="px-4 py-3 text-right font-semibold">Tickets</th>
                      <th className="px-4 py-3 text-right font-semibold">Avg ticket</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report?.top_services || []).map((row, index) => (
                      <tr
                        key={`${row.service_name}-${index}`}
                        className="border-t border-slate-100"
                      >
                        {showEntityColumns ? (
                          <>
                            <td className="px-4 py-3 text-slate-700">
                              {row.business_name || "--"}
                            </td>
                            <td className="px-4 py-3 text-slate-700">
                              {row.salon_name || "--"}
                            </td>
                          </>
                        ) : null}
                        <td className="px-4 py-3 text-slate-800">{row.service_name}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{row.ticket_count}</td>
                        <td className="px-4 py-3 text-right text-slate-700">
                          {formatMoney(row.avg_service_price)}
                        </td>
                      </tr>
                    ))}
                    {!loading && !(report?.top_services || []).length ? (
                      <tr>
                        <td
                          colSpan={showEntityColumns ? 5 : 3}
                          className="px-4 py-8 text-center text-slate-500"
                        >
                          No service rows for this date range.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
